import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Rfi } from '../objects/rfi';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class RfiService {
  private apiUrl = environment.apiUrl + 'Rfis';

  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  private rfisSubject = new BehaviorSubject<Rfi[]>([]);
  public rfis$ = this.rfisSubject.asObservable();

  constructor(private http: HttpClient) {}

  getByProject(projectId: number): Observable<Rfi[]> {
    return this.http
      .get<Rfi[]>(`${this.apiUrl}/project/${projectId}`)
      .pipe(
        tap(r => this.rfisSubject.next(r)),
        catchError(() => this.getMockData(projectId))
      );
  }

  create(rfi: Partial<Rfi>): Observable<Rfi> {
    return this.http.post<Rfi>(this.apiUrl, rfi, this.httpOptions).pipe(
      tap(r => this.rfisSubject.next([...this.rfisSubject.value, r])),
      catchError(err => {
        console.error('RFI create failed, saving locally', err);
        const local: Rfi = { ...rfi, id: Date.now() } as Rfi;
        this.rfisSubject.next([...this.rfisSubject.value, local]);
        return of(local);
      })
    );
  }

  update(id: number, rfi: Rfi): Observable<Rfi> {
    return this.http.put<Rfi>(`${this.apiUrl}/${id}`, rfi, this.httpOptions).pipe(
      tap(updated => {
        const current = this.rfisSubject.value.map(r => r.id === id ? updated : r);
        this.rfisSubject.next(current);
      }),
      catchError(err => {
        console.error('RFI update failed, updating locally', err);
        const current = this.rfisSubject.value.map(r => r.id === id ? rfi : r);
        this.rfisSubject.next(current);
        return of(rfi);
      })
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, this.httpOptions).pipe(
      tap(() => this.rfisSubject.next(this.rfisSubject.value.filter(r => r.id !== id))),
      catchError(err => {
        console.error('RFI delete failed, removing locally', err);
        this.rfisSubject.next(this.rfisSubject.value.filter(r => r.id !== id));
        return of(null);
      })
    );
  }

  getMockData(projectId: number): Observable<Rfi[]> {
    const today = new Date();
    const dueDate = new Date(today);
    dueDate.setDate(today.getDate() + 7);

    const mock: Rfi[] = [
      {
        id: 1,
        projectId,
        rfiNumber: 'RFI-001',
        subject: 'Beam size clarification at grid line B-4',
        description: 'Structural drawings show W12x50 but detail sheet calls out W14x53. Please clarify which beam size to use at column grid B-4 intersection.',
        submittedBy: 'Mike Johnson (Foreman)',
        submittedTo: 'ABC Engineering',
        dateSubmitted: today.toISOString().split('T')[0],
        dateRequired: dueDate.toISOString().split('T')[0],
        status: 'Open',
        ballInCourt: 'ABC Engineering',
        costImpact: 0,
        scheduleImpactDays: 0,
        createdAt: today.toISOString()
      },
      {
        id: 2,
        projectId,
        rfiNumber: 'RFI-002',
        subject: 'Window rough opening dimensions for west elevation',
        description: 'Architectural plans do not specify rough opening sizes for windows on the west elevation (W-1 through W-6). Need dimensions before framing can proceed.',
        submittedBy: 'Mike Johnson (Foreman)',
        submittedTo: 'Smith Architects',
        dateSubmitted: new Date(today.setDate(today.getDate() - 5)).toISOString().split('T')[0],
        dateRequired: new Date().toISOString().split('T')[0],
        dateAnswered: new Date().toISOString().split('T')[0],
        answer: 'All windows W-1 through W-6 to have RO of width + 2" and height + 2.5". See attached detail.',
        status: 'Answered',
        ballInCourt: 'Field',
        costImpact: 0,
        scheduleImpactDays: 1,
        createdAt: new Date(today.setDate(today.getDate() - 5)).toISOString()
      }
    ];
    this.rfisSubject.next(mock);
    return of(mock);
  }

  nextRfiNumber(rfis: Rfi[]): string {
    const count = rfis.length + 1;
    return `RFI-${count.toString().padStart(3, '0')}`;
  }
}
